import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { formatDisplayDate } from '~/shared/lib/date'
import type { PolicyConfigurationDetail } from './BenefitPolicyService'

interface ReviewPublishDialogProps {
  open: boolean
  policy: PolicyConfigurationDetail
  publishing: boolean
  refusal?: string
  onClose: () => void
  onConfirm: () => void
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="body2" sx={{ color: 'text.secondary', width: 160, flex: 'none' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Stack>
)

const period = (policy: PolicyConfigurationDetail) =>
  policy.effective_start && policy.effective_end
    ? `${formatDisplayDate(policy.effective_start)} – ${formatDisplayDate(policy.effective_end)}`
    : '—'

/**
 * The last look before a policy becomes member-visible.
 *
 * A published policy stays editable, but it cannot be returned to draft or discarded, and from here
 * on every save lands in front of enrolled members — so the values that decide what they see are
 * restated here rather than left on a form the operator has been scrolling through. Everything
 * shown is the server's own payload; the dialog computes nothing.
 */
export const ReviewPublishDialog = ({
  open,
  policy,
  publishing,
  refusal,
  onClose,
  onConfirm,
}: ReviewPublishDialogProps) => {
  const mapping = policy.plan_mappings[0]
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Review &amp; publish</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Publishing makes this policy visible to members for its effective period. It stays
          editable afterwards, but each change reaches members as soon as it is saved, and the
          policy can no longer be returned to draft or discarded.
        </DialogContentText>
        <Stack spacing={1} data-testid="review-summary">
          <Row label="Contract" value={policy.contract_name ?? '—'} />
          <Row label="Effective period" value={period(policy)} />
          <Row
            label="Electable from"
            value={
              policy.electable_start_date ? formatDisplayDate(policy.electable_start_date) : '—'
            }
          />
          <Row label="Coverage product" value={policy.coverage_product_name ?? '—'} />
          <Row
            label="Accumulator tiers"
            value={policy.accumulator_tiers ? `${policy.accumulator_tiers}-tier` : '—'}
          />
          <Row
            label="Plan mapping"
            value={mapping ? `${mapping.tpa_vendor} · ${mapping.alias_id}` : '—'}
          />
        </Stack>
        {policy.warnings.map(warning => (
          <Alert key={warning} severity="warning" sx={{ mt: 2 }} data-testid="review-warning">
            {warning}
          </Alert>
        ))}
        {refusal && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="review-refusal">
            {refusal}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={publishing}
          data-testid="review-publish-confirm"
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  )
}
